import { backUrl } from "../utils/backURL";
import { apiFetch } from "../utils/apiFetch";

/**
 * Gets the base URL for agent manager API requests (proxied through backend)
 * @returns {string} Backend API URL for agent manager routes
 */
const getAgentManagerUrl = () => {
  return `${backUrl}/agent-manager`;
};

/**
 * Gets the headers for agent manager API requests
 * @param {Object} additionalHeaders - Additional headers to include
 * @returns {Object} Headers object with Content-Type
 */
const getAgentManagerHeaders = (additionalHeaders = {}) => {
  return {
    "Content-Type": "application/json",
    ...additionalHeaders
  };
};

/**
 * Creates a new agent deployment in the agent manager using webhook pattern
 * @param {Object} deploymentData - Deployment configuration
 * @param {string} deploymentData.name - Name for the agent
 * @param {string} deploymentData.system_instructions - System instructions for the agent
 * @param {string} deploymentData.model - Model name (e.g., "gemini-2.5-pro")
 * @param {string} deploymentData.description - Description of the agent
 * @param {string[]} deploymentData.tools - List of tool names to filter from the MCP server (optional)
 * @param {string} deploymentData.agent_id - MongoDB _id of the agent (required)
 * @param {string} deploymentData.webhook_url - URL to call when deployment completes (required)
 * @returns {Promise<Object>} Response data
 */
export const createDeployment = async (deploymentData) => {
  try {
    const response = await apiFetch(`${getAgentManagerUrl()}/deployments`, {
      method: "POST",
      headers: getAgentManagerHeaders(),
      body: JSON.stringify(deploymentData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error creating deployment:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Lists all deployments from the agent manager
 * @returns {Promise<Object>} Response data with list of deployments
 */
export const getDeployments = async () => {
  try {
    const response = await apiFetch(`${getAgentManagerUrl()}/deployments`, {
      method: "GET",
      headers: getAgentManagerHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error fetching deployments:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Deletes a deployment by resource ID
 * @param {string} resourceId - Resource ID of the deployment to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteDeployment = async (resourceId) => {
  try {
    const response = await apiFetch(
      `${getAgentManagerUrl()}/deployments/${resourceId}`,
      {
        method: "DELETE",
        headers: getAgentManagerHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error deleting deployment:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Deletes multiple deployments by their resource IDs
 * @param {string[]} resourceIds - Array of resource IDs to delete
 * @returns {Promise<Object>} Response data with deleted and failed resources
 */
export const deleteDeploymentsBatch = async (resourceIds) => {
  try {
    const response = await apiFetch(
      `${getAgentManagerUrl()}/deployments/batch-delete`,
      {
        method: "POST",
        headers: getAgentManagerHeaders(),
        body: JSON.stringify({resource_ids: resourceIds})
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error deleting deployments in batch:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Creates a new session for a deployment
 * @param {string} resourceId - Resource ID of the deployment
 * @param {string} userId - User ID for the session (optional, defaults to 'test_user')
 * @returns {Promise<Object>} Response data with session information
 */
export const createSession = async (resourceId, userId = "test_user") => {
  try {
    const response = await apiFetch(
      `${getAgentManagerUrl()}/deployments/${resourceId}/sessions`,
      {
        method: "POST",
        headers: getAgentManagerHeaders(),
        body: JSON.stringify({user_id: userId})
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error creating session:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Lists all sessions for a deployment
 * @param {string} resourceId - Resource ID of the deployment
 * @param {string} userId - User ID to filter sessions (optional, defaults to 'test_user')
 * @returns {Promise<Object>} Response data with list of sessions
 */
export const getSessions = async (resourceId, userId = "test_user") => {
  try {
    const response = await apiFetch(
      `${getAgentManagerUrl()}/deployments/${resourceId}/sessions?user_id=${userId}`,
      {
        method: "GET",
        headers: getAgentManagerHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error fetching sessions:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Sends a message to a deployment session
 * @param {string} resourceId - Resource ID of the deployment
 * @param {string} sessionId - Session ID
 * @param {string} message - Message to send
 * @param {string} userId - User ID (optional, defaults to 'test_user')
 * @param {Array} files - Array of file objects with base64, mimetype, and name (optional)
 * @returns {Promise<Object>} Response data with events
 */
export const sendMessage = async (
  resourceId,
  sessionId,
  message,
  userId = "test_user",
  files = []
) => {
  try {
    const response = await apiFetch(
      `${getAgentManagerUrl()}/deployments/${resourceId}/sessions/${sessionId}/query`,
      {
        method: "POST",
        headers: getAgentManagerHeaders(),
        body: JSON.stringify({
          user_id: userId,
          message: message,
          files: files
        })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error sending message:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Creates a new test session for local agent testing (without deployment)
 * @param {Object} testData - Test agent configuration
 * @param {string} testData.name - Name for the agent
 * @param {string} testData.system_instructions - System instructions for the agent
 * @param {string} testData.model - Model name (e.g., "gemini-2.5-pro")
 * @param {string} testData.description - Description of the agent
 * @param {string[]} testData.tools - List of tool names to filter from the MCP server (optional)
 * @param {string} testData.user_id - User ID for the session (optional, defaults to 'test_user')
 * @returns {Promise<Object>} Response data with test_session_id
 */
export const createTestSession = async (testData) => {
  try {
    const response = await apiFetch(`${getAgentManagerUrl()}/test/sessions`, {
      method: "POST",
      headers: getAgentManagerHeaders(),
      body: JSON.stringify(testData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error creating test session:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Sends a message to a test session
 * @param {string} testSessionId - Test session ID
 * @param {string} message - Message to send
 * @param {Array} files - Array of file objects with base64, mimetype, and name (optional)
 * @returns {Promise<Object>} Response data with events
 */
export const sendTestMessage = async (testSessionId, message, files = []) => {
  try {
    const response = await apiFetch(
      `${getAgentManagerUrl()}/test/sessions/${testSessionId}/query`,
      {
        method: "POST",
        headers: getAgentManagerHeaders(),
        body: JSON.stringify({
          message: message,
          files: files
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error sending test message:", error);
    return {success: false, error: error.message};
  }
};

/**
 * Deletes a test session
 * @param {string} testSessionId - Test session ID to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteTestSession = async (testSessionId) => {
  try {
    const response = await apiFetch(
      `${getAgentManagerUrl()}/test/sessions/${testSessionId}`,
      {
        method: "DELETE",
        headers: getAgentManagerHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {success: true, data};
  } catch (error) {
    console.error("Error deleting test session:", error);
    return {success: false, error: error.message};
  }
};